import React, { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { Button, Form, Input, Switch } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { useNavigate } from 'react-router-dom';
import { InfiniteScroll, SelectWithSearch, Spin, WarningCard } from 'common/components';
import { EOrderDirection, EOrderField, ERoute } from 'common/const';
import { debounce, priceFormatter, renderSelectCustomOptionWithUser, showMessage, showSuccessMessage } from 'common/helpers';
import { useSubdivisionContext } from 'common/context';
import { DEFAULT_VALUE_0, LIST_LIMIT_0, PRICE_LIMIT } from 'common/config';
import { IFormValue } from 'common/models';
import { Bin, Plus } from 'common/svg';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import {
  getSubdivisionSettingsManagerOptions,
  getSubdivisionSettingsSubdivisionHeadOptions,
  getSubdivisionSettingsSubdivisionLegalEntityOptions,
} from 'entities/Settings/Settings.helper';
import { ESettingsMenuTab, ESettingsTab } from 'entities/Settings/Settings.enum';
import { SubdivisionCreateCard, SubdivisionLimitsChangeModal } from 'entities/Subdivision/components';
import { ISubdivision, IUpdateSubdivisionPayload } from 'entities/Subdivision/Subdivision.models';
import { findSubdivisionChildrenIds, calculateSubdivisionBalance } from 'entities/Subdivision/Subdivision.helper';
import { getLegalList, selectLegalList } from 'entities/LegalEntity/Legal.slice';
import {
  deleteSubdivision,
  filterSubdivisionList,
  selectIsSubdivisionLoading,
  selectSubdivisionList,
} from 'entities/Subdivision/Subdivision.slice';
import { getUserList, selectCurrentUser, selectUserList } from 'entities/User/User.slice';

interface IComponentProps {
  header: ReactNode;
  subdivision: ISubdivision | null;
  onSubdivisionUpdate: (payload: IUpdateSubdivisionPayload) => Promise<void>;
}

export const SettingsSubdivisionGeneralTab: FC<IComponentProps> = ({ header, subdivision, onSubdivisionUpdate }) => {
  const dispatch = useAppDispatch();
  // legal
  const legalList = useAppSelector(selectLegalList);
  // subdivision
  const subdivisionList = useAppSelector(selectSubdivisionList);
  const isSubdivisionLoading = useAppSelector(selectIsSubdivisionLoading);
  // user
  const currentUser = useAppSelector(selectCurrentUser);
  const userList = useAppSelector(selectUserList);

  const [fetchLoading, setFetchLoading] = useState<boolean>(true);
  const [openWarningCard, setOpenWarningCard] = useState<boolean>(false);
  const [openSubdivisionLimitsChangeModal, setOpenSubdivisionLimitsChangeModal] = useState<boolean>(false);
  const [form] = useForm();
  const navigate = useNavigate();
  const { setParentId, setOpenSubdivisionCreateCard } = useSubdivisionContext();

  const accountId = currentUser?.account?.id;
  const legalEntityOptions = useMemo(() => {
    return getSubdivisionSettingsSubdivisionLegalEntityOptions(legalList);
  }, [legalList]);
  const subdivisionHeadOptions = useMemo(() => {
    return getSubdivisionSettingsSubdivisionHeadOptions(userList);
  }, [userList]);
  const managerOptions = useMemo(() => {
    return getSubdivisionSettingsManagerOptions(userList);
  }, [userList]);
  const balance = calculateSubdivisionBalance(subdivision);

  const debouncedSubdivisionUpdate = debounce(onSubdivisionUpdate);

  const onFormValuesChange = (value: IFormValue) => {
    const successMessage = 'Подразделение успешно изменено.';

    if (subdivision) {
      if ('code' in value) {
        debouncedSubdivisionUpdate({
          id: subdivision.id,
          ...value,
          onSuccess: () => showSuccessMessage(successMessage),
        });
      } else if ('headOfDepartmentId' in value) {
        onSubdivisionUpdate({
          id: subdivision.id,
          headOfDepartmentId: value.headOfDepartmentId ? value.headOfDepartmentId : null,
          onSuccess: () => showSuccessMessage(successMessage),
        });
      } else {
        onSubdivisionUpdate({
          id: subdivision.id,
          ...value,
          onSuccess: () => showSuccessMessage(successMessage),
        });
      }
    }
  };

  const onDeleteSubdivisionConfirmClick = () => {
    if (subdivision) {
      dispatch(
        deleteSubdivision({
          id: subdivision.id,
          onSuccess: () => {
            const subdivisionAndChildrenIds = findSubdivisionChildrenIds(subdivision.id, subdivisionList);

            setOpenWarningCard(false);
            showMessage({
              content: `Подразделение ${subdivision.name} удалено.`,
              icon: <Bin className="icon-bin-dark-grey" />,
            });
            dispatch(filterSubdivisionList(subdivisionAndChildrenIds));

            if (subdivision.parentId) {
              navigate(`${ERoute.Settings}/${ESettingsTab.Subdivision}/${subdivision.parentId}/${ESettingsMenuTab.General}`);
            } else {
              navigate(`${ERoute.Settings}/${ESettingsTab.Account}/${ESettingsMenuTab.Contracts}`);
            }
          },
        }),
      );
    }
  };

  const onLimitsChange = (payload: { balance?: number | null; maxGoodPrice?: number | null }, onSuccess: () => void) => {
    if (!subdivision) {
      return;
    }

    let limit = null;

    if (payload.balance !== null && payload.balance !== undefined) {
      limit = Number(((subdivision.spentLimit ?? DEFAULT_VALUE_0) + Number(payload.balance)).toFixed(2));

      if (limit > PRICE_LIMIT) {
        limit = PRICE_LIMIT;
      }
    }

    onSubdivisionUpdate({
      id: subdivision.id,
      limit,
      maxGoodPrice: payload.maxGoodPrice,
      onSuccess: () => {
        onSuccess();
        showSuccessMessage('Подразделение успешно изменено.');
      },
    });
  };

  useEffect(() => {
    if (!fetchLoading && subdivision?.id) {
      form.setFieldsValue({
        legalId: subdivision.legalId,
        code: subdivision.code,
        headOfDepartmentId: subdivision.headOfDepartmentId,
        managerId: subdivision.managerId,
        limit: subdivision.limit,
        maxGoodPrice: subdivision.maxGoodPrice,
        requestsConfirmation: subdivision.requestsConfirmation,
      });
    }
  }, [fetchLoading, subdivision?.id]);

  useEffect(() => {
    const fetch = async () => {
      setFetchLoading(true);
      form.resetFields();
      await dispatch(getLegalList({ limit: LIST_LIMIT_0 }));

      if (accountId) {
        await dispatch(
          getUserList({
            accountId,
            orderField: EOrderField.LastName,
            orderDirection: EOrderDirection.ASC,
            limit: LIST_LIMIT_0,
          }),
        );
      }

      setFetchLoading(false);
    };

    fetch();
  }, [accountId, subdivision?.id]);

  return (
    <>
      <div className="relative h-full flex direction-column flex-1">
        <InfiniteScroll>
          <Form className="redesign mb-19" form={form} layout="vertical" onValuesChange={onFormValuesChange}>
            {header}

            <Spin spinning={fetchLoading}>
              <div style={{ maxWidth: 720 }}>
                <div className="mb-14 subdivision-general-settings__container">
                  <Form.Item className="mb-0" label="Юридическое лицо" name="legalId">
                    <SelectWithSearch placeholder="Выберите юр.лицо" options={legalEntityOptions} />
                  </Form.Item>

                  <Form.Item className="mb-0" label="Код подразделения" name="code">
                    <Input placeholder="Введите код подразделения" />
                  </Form.Item>

                  <Form.Item className="mb-0" name="headOfDepartmentId" label="Руководитель подразделения">
                    <SelectWithSearch
                      placeholder="Выберите руководителя подразделения"
                      options={subdivisionHeadOptions}
                      allowClear
                      optionRender={(option) => {
                        const user = userList.find((item) => item.id === option.value);

                        return user ? renderSelectCustomOptionWithUser(user) : option.label;
                      }}
                    />
                  </Form.Item>
                </div>

                <div className="mb-14">
                  <div className="mb-3 flex align-center gap-5">
                    <div className="ff-medium fs-m">Внутреннее согласование заказов</div>

                    <Form.Item noStyle name="requestsConfirmation" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                  </div>

                  <div className="mb-6 ff-regular fs-xs color-dark-grey">
                    Когда включена эта функция, заказы пользователей будут направляться на согласование менеджеру. Решение о
                    передаче заказа поставщику также будет приниматься менеджером.
                  </div>

                  {subdivision?.requestsConfirmation && (
                    <div className="subdivision-general-settings__container">
                      <Form.Item className="mb-0" label="Менеджер по закупкам" name="managerId">
                        <SelectWithSearch
                          placeholder="Выберите менеджера по закупкам"
                          options={managerOptions}
                          optionRender={(option) => {
                            const user = userList.find((item) => item.id === option.value);

                            return user ? renderSelectCustomOptionWithUser(user) : option.label;
                          }}
                        />
                      </Form.Item>
                    </div>
                  )}
                </div>

                <div>
                  <div className="mb-3 ff-medium fs-m">Лимиты на закупку</div>

                  <div className="mb-6 ff-regular fs-xs color-dark-grey">
                    Лимиты, установленные в настройках аккаунта, применены к подразделению по-умолчанию. Изменения лимитов в
                    настройках подразделения распространяются только на подразделение.
                  </div>

                  {(subdivision?.limit !== null || subdivision?.maxGoodPrice !== null) && (
                    <div className="mb-6 subdivision-general-settings__container">
                      {subdivision?.limit !== null && (
                        <div>
                          <div className="mb-2 ff-medium fs-s">{priceFormatter(subdivision?.limit)}</div>
                          <span className="ff-regular fs-xs color-dark-grey">Общий лимит подразделения</span>
                        </div>
                      )}

                      {balance !== null && (
                        <div>
                          <div className="mb-2 ff-medium fs-s">{priceFormatter(balance)}</div>
                          <span className="ff-regular fs-xs color-dark-grey">Остаток за период</span>
                        </div>
                      )}

                      {subdivision?.maxGoodPrice !== null && (
                        <div>
                          <div className="mb-2 ff-medium fs-s">{priceFormatter(subdivision?.maxGoodPrice)}</div>
                          <span className="ff-regular fs-xs color-dark-grey">Лимит цены на товар (включительно)</span>
                        </div>
                      )}
                    </div>
                  )}

                  {subdivision?.limit !== null || subdivision?.maxGoodPrice !== null ? (
                    <Button className="button-sm secondary" onClick={() => setOpenSubdivisionLimitsChangeModal(true)}>
                      Изменить лимиты
                    </Button>
                  ) : (
                    <Button className="button-sm primary" onClick={() => setOpenSubdivisionLimitsChangeModal(true)}>
                      Установить лимиты
                    </Button>
                  )}
                </div>
              </div>
            </Spin>
          </Form>
        </InfiniteScroll>

        <div className="absolute flex align-center justify-between wrap subdivision-general-settings__footer">
          <Button
            className="button-sm deleting"
            icon={<Bin className="icon-bin-pale-red" />}
            onClick={() => setOpenWarningCard(true)}
          >
            Удалить подразделение
          </Button>

          <Button
            className="button-sm primary"
            icon={<Plus className="icon-plus-white" />}
            onClick={() => {
              setParentId(subdivision?.id);
              setOpenSubdivisionCreateCard(true);
            }}
          >
            Дочернее подразделение
          </Button>
        </div>
      </div>

      <SubdivisionCreateCard />

      <WarningCard
        open={openWarningCard}
        content="Вы уверены, что хотите удалить подразделение?"
        subtitle="Отменить данное действие будет невозможно."
        confirmBtnTitle="Удалить подразделение"
        loading={isSubdivisionLoading}
        onConfirm={onDeleteSubdivisionConfirmClick}
        onClose={() => setOpenWarningCard(false)}
      />

      <SubdivisionLimitsChangeModal
        open={openSubdivisionLimitsChangeModal}
        loading={isSubdivisionLoading}
        subdivision={subdivision}
        onCancel={() => setOpenSubdivisionLimitsChangeModal(false)}
        onSubmit={onLimitsChange}
      />
    </>
  );
};
